// Fichier: /frontend/web/src/utils/roleUtils.js
import React from 'react';
import * as MuiIcons from '@mui/icons-material';
import { Badge as RoleIcon } from '@mui/icons-material';

// Fonction pour obtenir dynamiquement une icône MUI par son nom
export const getIconByName = (iconName, size = "small") => {
  if (!iconName) return <RoleIcon fontSize={size} />;
  
  try {
    // Essayer de récupérer l'icône par son nom
    const IconComponent = MuiIcons[iconName];
    return IconComponent ? <IconComponent fontSize={size} /> : <RoleIcon fontSize={size} />;
  } catch (error) {
    console.warn(`Icon ${iconName} not found`, error);
    return <RoleIcon fontSize={size} />;
  }
};

// Tableau des couleurs disponibles
const availableColors = [
  { name: 'red', value: '#f44336' },
  { name: 'pink', value: '#e91e63' },
  { name: 'purple', value: '#9c27b0' },
  { name: 'deepPurple', value: '#673ab7' },
  { name: 'indigo', value: '#3f51b5' },
  { name: 'blue', value: '#2196f3' },
  { name: 'lightBlue', value: '#03a9f4' },
  { name: 'cyan', value: '#00bcd4' },
  { name: 'teal', value: '#009688' },
  { name: 'green', value: '#4caf50' },
  { name: 'lightGreen', value: '#8bc34a' },
  { name: 'lime', value: '#cddc39' },
  { name: 'yellow', value: '#ffeb3b' },
  { name: 'amber', value: '#ffc107' },
  { name: 'orange', value: '#ff9800' },
  { name: 'deepOrange', value: '#ff5722' },
  { name: 'brown', value: '#795548' },
  { name: 'grey', value: '#9e9e9e' },
  { name: 'blueGrey', value: '#607d8b' }
];

// Fonction pour trouver la meilleure icône pour un rôle
export const findBestIconForRole = (roleName) => {
  if (!roleName) return 'Badge';
  
  // Cas spéciaux pour les rôles courants
  const specialCases = {
    'admin': 'AdminPanelSettings',
    'administrator': 'AdminPanelSettings',
    'owner': 'Home',
    'tenant': 'Apartment',
    'user': 'Person',
    'moderator': 'SupervisorAccount',
    'support': 'Support',
    'technician': 'Build',
    'manager': 'Business',
    'accountant': 'AccountBalance',
    'security': 'Security',
    'notification': 'Notifications',
    'forum': 'Forum',
    'group': 'Group',
    'vip': 'Star'
  };
  
  // Vérifier si le rôle correspond à un cas spécial
  const lowerRoleName = roleName.toLowerCase();
  for (const [key, value] of Object.entries(specialCases)) {
    if (lowerRoleName.includes(key)) {
      return value;
    }
  }
  
  // Si aucune correspondance n'est trouvée, retourner une icône par défaut
  return 'Badge';
};

// Fonction pour trouver la couleur la plus appropriée pour un rôle
export const findBestColorForRole = (roleName) => {
  if (!roleName) return '#9e9e9e'; // Gris par défaut
  
  // Cas spéciaux pour les rôles courants
  const specialCases = {
    'admin': '#f44336', // Rouge
    'administrator': '#f44336', // Rouge
    'owner': '#2196f3', // Bleu
    'tenant': '#4caf50', // Vert
    'user': '#9e9e9e', // Gris
    'moderator': '#ff9800', // Orange
    'support': '#03a9f4', // Bleu clair
    'technician': '#795548', // Marron
    'manager': '#673ab7', // Violet
    'accountant': '#009688', // Turquoise
    'security': '#e91e63', // Rose
    'notification': '#ff5722', // Orange foncé
    'forum': '#607d8b', // Bleu-gris
    'group': '#3f51b5', // Indigo
    'vip': '#ffc107' // Jaune
  };
  
  // Vérifier si le rôle correspond à un cas spécial
  const lowerRoleName = roleName.toLowerCase();
  for (const [key, value] of Object.entries(specialCases)) {
    if (lowerRoleName.includes(key)) {
      return value;
    }
  }
  
  // Sinon, attribuer une couleur en fonction de la première lettre du rôle
  const firstChar = lowerRoleName.charAt(0);
  const index = firstChar.charCodeAt(0) % availableColors.length;
  return availableColors[index].value;
};

// Fonction pour construire dynamiquement le roleMapping à partir des rôles de la base de données
export const buildRoleMapping = (roles) => {
  const mapping = {};
  
  if (!roles || !Array.isArray(roles)) return mapping;
  
  roles.forEach(role => {
    // Si le rôle a déjà une icône et une couleur, les utiliser
    // Sinon, trouver les meilleures correspondances
    const iconName = role.icon || findBestIconForRole(role.name);
    mapping[role.name] = {
      color: role.color || findBestColorForRole(role.name),
      iconName: iconName, // Stocker le nom de l'icône
      label: role.description || role.name
    };
  });
  
  return mapping;
};

// Fonction pour obtenir l'icône d'un rôle
export const getRoleIcon = (roleOrName, roleMapping) => {
  
  // Si roleOrName est une chaîne, créer un objet rôle temporaire
  const role = typeof roleOrName === 'string' ? { name: roleOrName } : roleOrName;
  
  if (!role) return getIconByName('Badge');
  
  // Si le rôle a une propriété icon, l'utiliser
  if (role.icon) {
    return getIconByName(role.icon);
  }
  
  // Si le rôle est dans le mapping, utiliser l'icône du mapping
  if (roleMapping && roleMapping[role.name] && roleMapping[role.name].iconName) {
    return getIconByName(roleMapping[role.name].iconName);
  }
  
  // Sinon, trouver la meilleure icône
  return getIconByName(findBestIconForRole(role.name));
};

// Fonction pour obtenir la couleur d'un rôle
export const getRoleColor = (roleOrName, roleMapping) => {
  console.log('//////////////+++++++++getRoleColor',roleMapping);
  // Si roleOrName est une chaîne, créer un objet rôle temporaire
  const role = typeof roleOrName === 'string' ? { name: roleOrName } : roleOrName;
  
  if (!role) return '#9e9e9e'; // Gris par défaut
  
  // Si le rôle a une propriété color, l'utiliser
  if (role.color) {
    return role.color;
  }
  
  // Si le rôle est dans le mapping, utiliser la couleur du mapping
  if (roleMapping && roleMapping[role.name] && roleMapping[role.name].color) {
    return roleMapping[role.name].color;
  }
  
  // Sinon, trouver la meilleure couleur
  return findBestColorForRole(role.name);
};

// Fonction pour obtenir le libellé d'un rôle
export const getRoleLabel = (roleOrName, roleMapping) => {
  // Si roleOrName est une chaîne, créer un objet rôle temporaire
  const role = typeof roleOrName === 'string' ? { name: roleOrName } : roleOrName;
  
  if (!role) return '';

  // Si le rôle a une propriété description, l'utiliser
  if (role.description && role.description.trim() !== '') {
    return role.description;
  }
  
  // Si le rôle est dans le mapping, utiliser le libellé du mapping
  if (roleMapping && roleMapping[role.name] && roleMapping[role.name].label) {
    return roleMapping[role.name].label;
  }
  
  // Sinon, utiliser le nom du rôle
  return role.name;
};